import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type {
  Basis,
  CalcMethod,
  ChargeInput,
  Confidence,
  InferenceSource,
} from '@everplan/shared';
import { ChargeFormulaBadge } from './ChargeFormulaBadge';
import { ChargeFormulaEditor } from './ChargeFormulaEditor';

type ChargeData = Omit<ChargeInput, 'sortOrder'>;

export interface ChargeFormulaContext {
  /** Stable id used by the editor to identify this row's own charge so it
   *  can hide itself from the sibling-charge basis picker. Pass the persisted
   *  UUID when editing an existing charge, or a synthesized form-scoped id
   *  (e.g. `form-charge-${index}`) for unsaved rows. */
  selfId: string;
  /** Other charges in the same package (the row's own charge can be included;
   *  the editor filters it out via `selfId`). Each entry carries the sibling's
   *  current `basis`/`calcMethod` so we can detect cross-charge dependencies
   *  for the cascade-on-delete confirmation. */
  siblings: Array<{
    id: string;
    description: string | null;
    type: string;
    calcMethod?: CalcMethod;
    basis?: Basis | null;
    rateBps?: number | null;
  }>;
  /** Deduped sectionNormalized values from line items + add-ons. */
  availableSections: string[];
  /** Live package context used to render computed previews in the editor. */
  packageContext: {
    lineItemTotal: number;
    addOnTotal: number;
    sectionTotals: Record<string, number>;
    siblingResolvedAmounts: Record<string, number>;
  };
  /** Optional pretty labels for badge basis rendering. */
  sectionLabels?: Record<string, string>;
  chargeLabels?: Record<string, string>;
}

interface ChargeRowProps {
  charge: ChargeData;
  onChange: (updated: ChargeData) => void;
  onRemove: () => void;
  /** When provided, the formula badge + inline editor are rendered. Omit for
   *  display-only contexts that don't have package context to thread in. */
  formulaContext?: ChargeFormulaContext;
  /** Controlled expanded state. When omitted, the row falls back to local
   *  state — used in display-only contexts that don't need to coordinate
   *  expansion across rows. */
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

const CHARGE_TYPE_LABELS: Record<ChargeInput['type'], string> = {
  delivery: 'Delivery',
  labor: 'Labor',
  tax: 'Tax',
  other: 'Other',
};

/**
 * Walk a basis tree and collect referenced charge ids. Same shape as the
 * backend's `dependsOn` traversal — kept inline here since it's small and
 * tightly coupled to the cascade-confirm UX.
 */
function collectChargeRefs(basis: Basis | null | undefined): string[] {
  if (!basis) return [];
  switch (basis.kind) {
    case 'charges':
      return basis.chargeIds;
    case 'composite':
      return basis.parts.flatMap(collectChargeRefs);
    default:
      return [];
  }
}

interface Dependent {
  id: string;
  label: string;
}

function findDependents(
  thisId: string,
  siblings: ChargeFormulaContext['siblings'],
): Dependent[] {
  const dependents: Dependent[] = [];
  for (const sib of siblings) {
    if (sib.id === thisId) continue;
    const refs = collectChargeRefs(sib.basis ?? null);
    if (refs.includes(thisId)) {
      const desc = sib.description?.trim() || sib.type;
      const rate =
        sib.calcMethod === 'percentage' && sib.rateBps != null
          ? ` (${(sib.rateBps / 100).toFixed(2).replace(/\.00$/, '')}%)`
          : '';
      dependents.push({ id: sib.id, label: `${desc}${rate}` });
    }
  }
  return dependents;
}

export function ChargeRow({
  charge,
  onChange,
  onRemove,
  formulaContext,
  expanded: controlledExpanded,
  onToggleExpanded,
}: ChargeRowProps) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : localExpanded;

  const calcMethod: CalcMethod = charge.calcMethod ?? 'flat';
  const rateBps = charge.rateBps ?? null;
  const basis = (charge.basis as Basis | null | undefined) ?? null;
  const inferenceSource: InferenceSource = charge.inferenceSource ?? 'legacy';
  const confidence: Confidence | null = charge.confidence ?? null;

  const showBadge = !!formulaContext && calcMethod === 'percentage';
  const canExpand = !!formulaContext;

  const toggleExpanded = () => {
    if (isControlled) {
      onToggleExpanded?.();
    } else {
      setLocalExpanded((v) => !v);
    }
  };

  // Cascade detection: walk siblings to find any whose basis references this
  // charge. Only meaningful when we have a formulaContext (read-only contexts
  // can't know dependents from the row alone).
  const dependents: Dependent[] = formulaContext
    ? findDependents(formulaContext.selfId, formulaContext.siblings)
    : [];

  const handleDeleteClick = () => {
    if (dependents.length > 0) {
      setConfirmOpen(true);
    } else {
      onRemove();
    }
  };

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    onRemove();
  };

  return (
    <div className="rounded px-1 -mx-1">
      <div className="group flex items-center gap-1.5 py-1 hover:bg-muted/30 rounded">
        {/* Expand chevron — only visible when formula context is provided */}
        {canExpand ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={toggleExpanded}
            aria-label={expanded ? 'Collapse formula editor' : 'Edit formula'}
            className="shrink-0 text-muted-foreground"
          >
            {expanded ? (
              <ChevronDown className="size-3" />
            ) : (
              <ChevronRight className="size-3" />
            )}
          </Button>
        ) : null}

        {/* Type */}
        <Select
          value={charge.type}
          onValueChange={(val) =>
            onChange({ ...charge, type: val as ChargeInput['type'] })
          }
        >
          <SelectTrigger size="sm" className="w-24 shrink-0 h-7 border-transparent bg-transparent hover:border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(CHARGE_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Description */}
        <Input
          value={charge.description ?? ''}
          onChange={(e) =>
            onChange({ ...charge, description: e.target.value || null })
          }
          placeholder="Description"
          className="h-7 text-sm flex-1 min-w-0 border-transparent bg-transparent hover:border-border focus:border-border"
        />

        {/* Amount */}
        <CurrencyInput
          valueCents={charge.amount}
          onChangeCents={(cents) =>
            // Editing the dollar amount of a flat charge is NOT a formula
            // change — don't flip inferenceSource. For percentage charges the
            // amount field is effectively read-only (engine recomputes on
            // save) so user edits here are rare; we still preserve the
            // existing provenance.
            onChange({ ...charge, amount: cents ?? 0 })
          }
          placeholder="0.00"
          className="h-7 w-24 border-transparent bg-transparent hover:border-border focus:border-border"
        />

        {/* Formula badge — sits between amount and remove. Hidden when flat. */}
        {showBadge ? (
          <div className="shrink-0">
            <ChargeFormulaBadge
              calcMethod={calcMethod}
              rateBps={rateBps}
              basis={basis}
              inferenceSource={inferenceSource}
              confidence={confidence}
              sectionLabels={formulaContext.sectionLabels}
              chargeLabels={formulaContext.chargeLabels}
              packageContext={formulaContext.packageContext}
              onClick={toggleExpanded}
            />
          </div>
        ) : null}

        {/* Remove */}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={handleDeleteClick}
          aria-label="Remove charge"
          className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-3" />
        </Button>
      </div>

      {/* Inline formula editor */}
      {expanded && formulaContext ? (
        <div className="ml-6 my-1.5">
          <ChargeFormulaEditor
            value={{
              calcMethod,
              rateBps,
              basis,
              flatAmount: charge.amount,
            }}
            siblings={formulaContext.siblings}
            selfId={formulaContext.selfId}
            availableSections={formulaContext.availableSections}
            packageContext={formulaContext.packageContext}
            onChange={(next) => {
              const formulaChanged =
                next.calcMethod !== calcMethod ||
                next.rateBps !== rateBps ||
                JSON.stringify(next.basis) !== JSON.stringify(basis);

              const updated: ChargeData = { ...charge };

              if (next.calcMethod === 'flat') {
                updated.calcMethod = 'flat';
                updated.rateBps = null;
                updated.basis = null;
                updated.amount = next.flatAmount;
              } else {
                updated.calcMethod = 'percentage';
                updated.rateBps = next.rateBps;
                updated.basis = next.basis;
                // Preserve `amount` — the engine will resolve it for display
                // and the backend recomputes on write.
              }

              if (formulaChanged) {
                updated.inferenceSource = 'user-overridden';
                // Drop confidence; user edits don't carry an inferred
                // confidence anymore.
                updated.confidence = null;
              }

              onChange(updated);
            }}
          />
        </div>
      ) : null}

      {/* Cascade-on-delete confirmation. Only mounted when at least one
          sibling depends on this charge — the backend already detaches them
          to flat amounts (Phase 5.5); the modal just adds the user
          confirmation step. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this charge?</AlertDialogTitle>
            <AlertDialogDescription>
              {dependents.length === 1
                ? '1 charge depends on this charge. Deleting will detach it as a flat amount.'
                : `${dependents.length} charges depend on this charge. Deleting will detach them as flat amounts.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dependents.length > 0 ? (
            <ul className="ml-4 list-disc text-sm text-muted-foreground">
              {dependents.map((d) => (
                <li key={d.id}>{d.label}</li>
              ))}
            </ul>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleConfirmDelete}>
              Delete &amp; detach
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
